import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  CircularProgress, 
  Alert,
  FormControlLabel,
  Switch,
  Stack,
  Tooltip,
  Snackbar,
  IconButton,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DashboardIcon from '@mui/icons-material/Dashboard';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import dashboardsService, { Category, Dashboard } from './services/DashboardsService';
import DashboardCategories from './components/DashboardCategories';
import Breadcrumb, { BreadcrumbItem } from '../../components/Breadcrumb';
import CategoryForm from './components/CategoryForm';
import SiteDowntimeSection from './components/SiteDowntimeSection';

const DashboardsPage: React.FC = () => {
  // Breadcrumb items for dashboard management
  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: 'Dashboards',
      path: '/dashboards',
      icon: <DashboardIcon sx={{ fontSize: '0.9rem' }} />
    }
  ];

  const [categories, setCategories] = useState<Category[]>([]);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeactivated, setShowDeactivated] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState<number>(0);
  const [isCreatingCategory, setIsCreatingCategory] = useState<boolean>(false);

  // Show a success message
  const showSuccess = useCallback((message: string) => {
    setSuccessMessage(message);
    // Auto-hide after 5 seconds
    setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);
  }, []);

  // Handle closing the success snackbar
  const handleCloseSuccess = () => {
    setSuccessMessage(null);
  };

  // Force refresh the data
  const refreshData = useCallback(() => {
    console.log("Refreshing dashboard data...");
    setRefreshCounter(prev => prev + 1);
  }, []);

  // Initial and refresh data load
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Don't show loading indicator on refresh to avoid UI disruption
        if (refreshCounter === 0) {
          setLoading(true);
        }
        setError(null);
        
        console.log("Fetching fresh dashboard data...");
        
        // Fetch categories from API
        const categoryData = await dashboardsService.getDashboardCategories(showDeactivated);
        
        if (Array.isArray(categoryData) && categoryData.length > 0) {
          console.log(`Loaded ${categoryData.length} categories`);
          setCategories(categoryData);
        } else {
          console.warn('No categories returned from API or invalid format');
          setCategories([]);
        }
        
        // Fetch dashboards from API
        const dashboardData = await dashboardsService.getDashboards(showDeactivated);
        
        if (Array.isArray(dashboardData)) {
          // Fix any dashboards that might have incomplete category references
          const fixedDashboards = dashboardData.map(dashboard => {
            // If dashboard has no category or invalid category structure
            if (!dashboard.category || typeof dashboard.category !== 'object' || !dashboard.category.id) {
              // If there's a categoryId as a direct property
              if (dashboard.category && dashboard.category.id) {
                // Find matching category
                const matchingCategory = categoryData.find(cat => cat.id === dashboard.category.id);
                if (matchingCategory) {
                  return { ...dashboard, category: matchingCategory };
                }
              }
            }
            return dashboard;
          });
          
          console.log(`Loaded ${fixedDashboards.length} dashboards`);
          setDashboards(fixedDashboards);
        } else {
          console.warn('No dashboards returned from API or invalid format');
          setDashboards([]);
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showDeactivated, refreshCounter]);

  const handleShowDeactivatedToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowDeactivated(event.target.checked);
  };

  const handleCreateCategory = async (category: Omit<Category, 'id'>) => {
    try {
      const result = await dashboardsService.createCategory(category);
      if (result) {
        showSuccess('Category created successfully.');
        refreshData();
        setIsCreatingCategory(false);
        return true;
      } else {
        setError('Failed to create category. Please try again.');
        return false;
      }
    } catch (error) {
      console.error('Error creating category:', error);
      setError('Failed to create category. Please try again.');
      return false;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Add Breadcrumb component */}
      <Breadcrumb items={breadcrumbItems} />
      
      {/* Modern controls bar */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Tooltip title="Refresh dashboard data">
            <IconButton 
              onClick={refreshData}
              color="primary"
              disabled={loading}
              sx={{ 
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : 'rgba(33, 150, 243, 0.08)',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.12)' : 'rgba(33, 150, 243, 0.12)',
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          
          <Tooltip title={showDeactivated ? "Showing both active and deactivated items" : "Showing only active items"}>
            <FormControlLabel
              control={
                <Switch
                  checked={showDeactivated}
                  onChange={handleShowDeactivatedToggle}
                  color="primary"
                  icon={<VisibilityOffIcon fontSize="small" />}
                  checkedIcon={<VisibilityIcon fontSize="small" />}
                  sx={{ mx: 1 }}
                />
              }
              label={
                <Typography variant="button" sx={{ fontWeight: 500 }}>
                  Show All
                </Typography>
              }
            />
          </Tooltip>
        </Stack>
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreatingCategory(true)}
          sx={{ 
            borderRadius: '8px',
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
            }
          }}
        >
          Create New Category
        </Button>
      </Paper>

      {isCreatingCategory && (
        <CategoryForm
          onSave={handleCreateCategory}
          onCancel={() => setIsCreatingCategory(false)}
          initialValues={{
            name: '',
            shortname: '',
            subtitle: '',
            orderBy: categories.length + 1,
            deactivated: false
          }}
          isNew
        />
      )}

      {showDeactivated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing all dashboards and categories, including deactivated items.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && refreshCounter > 0 && (
        <Box sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {loading && refreshCounter === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <DashboardCategories 
          categories={categories} 
          dashboardsByCategory={dashboards.reduce((acc: Record<number, Dashboard[]>, dashboard) => {
            // Check if dashboard has a valid category object with an id
            if (dashboard.category && typeof dashboard.category === 'object' && dashboard.category.id) {
              const categoryId = dashboard.category.id;
              if (!acc[categoryId]) {
                acc[categoryId] = [];
              }
              acc[categoryId].push(dashboard);
            } else if (dashboard.category && dashboard.category.id) {
              // Alternative: check if dashboard has a categoryId property directly
              const categoryId = dashboard.category.id;
              if (!acc[categoryId]) {
                acc[categoryId] = [];
              }
              acc[categoryId].push(dashboard);
            } else {
              console.warn('Dashboard without valid category found:', dashboard);
            }
            return acc;
          }, {})}
          onUpdateCategory={async (updatedCategory) => {
            try {
              const originalCategory = categories.find(c => c.id === updatedCategory.id);
              const isDeactivating = originalCategory && 
                !originalCategory.deactivated && 
                updatedCategory.deactivated;
              
              if (isDeactivating) {
                // Use DELETE method for deactivation
                await dashboardsService.deactivateCategory(updatedCategory.id);
                showSuccess('Category deactivated successfully.');
              } else {
                // Use PUT method for all other updates (including reactivation)
                await dashboardsService.updateCategory(updatedCategory);
                showSuccess('Category updated successfully.');
              }
              
              refreshData();
              return true;
            } catch (error) {
              console.error('Error updating category:', error);
              setError(error instanceof Error ? error.message : 'Failed to update category. Please try again.');
              return false;
            }
          }}
          onCreateCategory={handleCreateCategory}
          onUpdateDashboard={async (updatedDashboard) => {
            try {
              const originalDashboard = dashboards.find(d => d.id === updatedDashboard.id);
              const isDeactivating = originalDashboard && 
                !originalDashboard.deactivated && 
                updatedDashboard.deactivated;
              
              if (isDeactivating) {
                // Use DELETE method for deactivation
                await dashboardsService.deactivateDashboard(updatedDashboard.id);
                showSuccess('Dashboard deactivated successfully.');
              } else {
                // Use PUT method for all other updates (including reactivation)
                await dashboardsService.updateDashboard(updatedDashboard);
                showSuccess('Dashboard updated successfully.');
              }
              
              refreshData();
              return true;
            } catch (error) {
              console.error('Error updating dashboard:', error);
              setError(error instanceof Error ? error.message : 'Failed to update dashboard. Please try again.');
              return false;
            }
          }}
          onCreateDashboard={async (newDashboard) => {
            try {
              const result = await dashboardsService.createDashboard(newDashboard);
              if (result) {
                showSuccess('Dashboard created successfully.');
                refreshData();
                return true;
              } else {
                setError('Failed to create dashboard. Please try again.');
                return false;
              }
            } catch (error) {
              console.error('Error creating dashboard:', error);
              setError('Failed to create dashboard. Please try again.');
              return false;
            }
          }}
          hideCreateCategoryButton={true} /* Hide the create category button in the component since we moved it to the top bar */
        />
      )}

      <SiteDowntimeSection />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={handleCloseSuccess}
        message={successMessage}
      />
    </Container>
  );
};

export default DashboardsPage;